/**
 * Admin session state. The cookie itself is httpOnly and invisible to this
 * code; this store only tracks whatever `GET /auth/me` told us, refreshed on
 * boot and after login/logout.
 */

import { create } from 'zustand';
import { ApiError } from '../lib/apiClient.js';
import { authApi } from '../api/resources.js';
import type { MeResponse } from '../api/types.js';

/**
 * `unreachable` is deliberately NOT `anonymous`. See `checkSession`.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous' | 'unreachable';

interface AuthState {
  status: AuthStatus;
  user: MeResponse | null;
  error: 'invalid_credentials' | 'network' | null;
  /** Consecutive session checks that could not get an answer. Drives the retry backoff. */
  sessionAttempts: number;
  checkSession(): Promise<void>;
  login(email: string, password: string): Promise<boolean>;
  logout(): Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'checking',
  user: null,
  error: null,
  sessionAttempts: 0,

  /**
   * Asks the server who we are, and — the point of this function — treats
   * "you are not signed in" and "I could not tell you" as different answers.
   *
   * This used to be a bare `catch` that set `anonymous`, so ANY failure threw
   * away a valid session: `RequireAuth` renders `<Navigate to="/login">` on
   * `anonymous`, so one 429 from the rate limiter, one 500, one dropped
   * connection, and the admin was on the login screen with their work gone.
   * That is not hypothetical — the live limiter is 100 requests/minute across
   * all of /api, which an admin clicking briskly through the catalog can reach.
   *
   * In the browser suite the same mechanism produced the "signed in via
   * storageState, but back at the login form" failures: the harness performs
   * exactly one sign-in per run and cannot recover from being logged out, so a
   * transient blip became a test failure blaming whichever page it landed on.
   * OneDev #27.
   *
   * Only a 401 is the server saying you are not signed in. Everything else
   * means the question is unanswered, which is what `unreachable` is for — the
   * session is kept, and the caller retries.
   */
  async checkSession() {
    try {
      const user = await authApi.me();
      set({ status: 'authenticated', user, error: null, sessionAttempts: 0 });
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized') {
        set({ status: 'anonymous', user: null, sessionAttempts: 0 });
        return;
      }
      set((s) => ({ status: 'unreachable', user: null, sessionAttempts: s.sessionAttempts + 1 }));
    }
  },

  async login(email, password) {
    set({ error: null });
    try {
      await authApi.login({ email, password });
      const user = await authApi.me();
      set({ status: 'authenticated', user, error: null });
      return true;
    } catch (err) {
      // Deliberately identical messaging whether the email is unknown or the
      // password is wrong — see docs/decisions/2026-07-31-login-error-messaging.md.
      if (err instanceof ApiError && err.kind === 'network') {
        set({ error: 'network' });
      } else {
        set({ error: 'invalid_credentials' });
      }
      return false;
    }
  },

  async logout() {
    try {
      await authApi.logout();
    } finally {
      // Signing out is the one place `anonymous` is set without asking the
      // server: the admin said so, and the intent stands even if the request
      // to drop the cookie server-side did not land.
      set({ status: 'anonymous', user: null, sessionAttempts: 0 });
    }
  },
}));
